import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FaCalendarCheck,
  FaSortDown,
} from 'react-icons/fa';
import { IoFilter } from 'react-icons/io5';
// eslint-disable-next-line import/no-extraneous-dependencies
import { format, isSameDay } from 'date-fns';

import {
  createFavoriteWeekFromPeriodAction,
  deleteAllShiftsAction,
  fetchShiftsFilteredQuietAction,
  getShiftsListAction,
  getWeekOptionsAction,
  postPublishWeekAction,
  postShiftsFilteredAction,
} from '../../actions/shift';
import { getAllStructuresAction, getStructureAction, getStructurePoolsAction } from '../../actions/structures';
import {
  GET_NOTE, SET_PLANNING_VIEW, SET_USERS_FILTERS, SET_WEEK_VIEW,
} from '../../actions/types';

import { postUsersFilteredAction } from '../../actions/users';
import DropDown from '../../components/DropDown/DropDown';

import Header from '../../components/Header';
import PlanningRow from '../../components/PlanningRow';
import { DayType, IShift } from '../../types/shift';

import {
  ILine, IPole, IPool, IStructure,
} from '../../types/structure';
import { IUser } from '../../types/user';

import calendar from '../../assets/images/calendar.svg';
import useWindowSize from '../../hooks/useWindowSize';
import SelectDate from '../../components/SelectDate/SelectDate';
import FiltersMobile from '../../components/FiltersMobile/FiltersMobile';

import styles from './AdminPlanning.module.scss';
import templateStyles from './WeekTemplatesTab.module.scss';
import ModalStandalone, { IModalHandle } from '../../lib/ModalStandalone';
import ImportModal from '../../components/ImportModal/ImportModal';
import ExportButton from '../../components/ExportButton';
import DayPicker from '../../components/DayPicker';
import { postContractsFilteredAction } from '../../actions/contract';
import { getHolidaysAction } from '../../actions/holiday';
// import { IHoliday } from '../../types/holiday';
import { postNotesFilteredAction } from '../../actions/note';
import { getManagementLeavesAction } from '../../actions/leave';
import NoteModal from '../../components/NoteModal';
import { getActiveContractsBetweenDates, getWeekNumber } from '../../utils';
import {
  leaveOverlapsViewRange,
  leavePictoKindFromStatus,
  userIdFromLeaveUser,
} from '../../utils/leavePlanning';
import { IContract } from '../../types/contract';
import type { ILeave } from '../../types/leave';
import Tooltip from '../../components/Toolip';
import ConfirmModal from '../../components/ConfirmModal';
import Loader from '../../components/Loader';
import WeekTemplatesTab from './WeekTemplatesTab';

const AdminPlanning = () => {
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const isMobile = width && width < 1024;

  const { user } = useSelector((store: any) => store.authReducer);
  const { usersList, filters } = useSelector((store: any) => store.usersReducer);
  const { note } = useSelector((store: any) => store.notesReducer);
  // const { holidays } = useSelector((store: any) => store.holidayReducer);
  const { structuresList, pools } = useSelector((store: any) => store.structureReducer);
  const { contracts } = useSelector((store: any) => store.contractsReducer);

  const {
    shiftsList, list, isWeekView, planningView, isLoading,
  } = useSelector((store: any) => store.shiftsReducer);
  const { managementLeaves } = useSelector((store: any) => store.leaveReducer);

  const modalRef = useRef<IModalHandle | any>(null);
  const noteModalRef = useRef<IModalHandle | any>(null);
  const confirmModalRef = useRef<IModalHandle | any>(null);
  const copyModalRef = useRef<IModalHandle | null>(null);

  const [filtersIsOpen, setFiltersIsOpen] = useState(false);
  const [week, setWeek] = useState<DayType[]>([]);
  const [copyWeekName, setCopyWeekName] = useState('');
  const [weekShiftsForTotals, setWeekShiftsForTotals] = useState<IShift[] | null>(null);

  const date = new Date(filters.startDate);
  const dayOfWeek = date.getUTCDay();
  const diff = (dayOfWeek === 1) ? 0 : dayOfWeek - 1;
  const monday = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate() - (diff === -1 ? 6 : diff),
  ));
  const sunday = new Date(Date.UTC(
    monday.getUTCFullYear(),
    monday.getUTCMonth(),
    monday.getUTCDate() + 6,
  ));
  const today = new Date();
  const isImport = today.getTime() < new Date(sunday).getTime();
  const isPublished = shiftsList?.every((s: IShift) => s.isPublished === true) ?? false;
  const canManageWeekTemplates = user?.role === 'Admin'
    || (user?.role !== 'Member' && user?.isReadOnly === false && user?.canManageWeekTemplates === true);
  const showCopyWeekToTemplateButton = planningView === 'global' && isWeekView && canManageWeekTemplates;
  const showListActions = !isMobile && (showCopyWeekToTemplateButton || user?.role === 'Admin');

  const structure = structuresList?.find((s: IStructure) => s._id === filters.structure);
  const polesList = structuresList?.find((s: IStructure) => s._id === filters.structure)?.poles;
  const poles = polesList?.filter((po: IPole) => {
    if (user?.role === 'Manager') {
      if (user?.poles.find((p: string) => p === po._id)) {
        return po;
      }
      return null;
    }
    return po;
  });

  const dateFormated = filters?.startDate && new Intl.DateTimeFormat('fr', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(filters?.startDate));

  const mondayFormated = filters?.startDate && new Intl.DateTimeFormat('fr', {
    day: 'numeric',
    month: 'long',
  }).format(new Date(filters?.startDate));

  const sundayFormated = filters?.endDate && new Intl.DateTimeFormat('fr', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(filters?.endDate));

  const weekNumber = getWeekNumber(sunday);

  const savedStructure = localStorage.getItem('structure');
  const activesContracts = getActiveContractsBetweenDates(
    contracts || [],
    filters.startDate,
    filters?.endDate || filters.startDate,
  );

  const usersSorted = useMemo(() => {
    const users: IUser[] = [];
    if (!poles || !usersList) {
      return users;
    }
    poles.forEach((po: IPole) => {
      usersList.forEach((u: IUser) => {
        const userFound = users.find((ur: IUser) => ur._id === u._id);
        const poleFound = u.poles.find((s: string) => s === po._id);
        if (!userFound && poleFound) {
          users.push(u);
        }
      });
    });
    return users;
  }, [usersList, poles]);

  const leavePictoByUserId = useMemo(() => {
    const map: Record<string, 'approved' | 'pending'> = {};
    if (!managementLeaves?.length || !filters.startDate) {
      return map;
    }
    const viewStart = filters.startDate;
    const viewEnd = filters.endDate || filters.startDate;
    managementLeaves.forEach((leave: ILeave) => {
      if (!leaveOverlapsViewRange(leave, viewStart, viewEnd)) return;
      const kind = leavePictoKindFromStatus(leave.status);
      if (!kind) return;
      const uid = userIdFromLeaveUser(leave.user);
      if (!uid) return;
      if (kind === 'approved') {
        map[uid] = 'approved';
      } else if (map[uid] !== 'approved') {
        map[uid] = 'pending';
      }
    });
    return map;
  }, [managementLeaves, filters.startDate, filters.endDate]);

  const mondayIso = monday.toISOString();
  const sundayIso = sunday.toISOString();

  useEffect(() => {
    let cancelled = false;
    async function loadWeekShiftsForTotals() {
      if (!filters.structure || filters.endDate) {
        setWeekShiftsForTotals(null);
        return;
      }
      const shifts = await fetchShiftsFilteredQuietAction(dispatch, {
        structure: filters.structure,
        startDate: mondayIso,
        endDate: sundayIso,
      });
      if (!cancelled) {
        setWeekShiftsForTotals(shifts);
      }
    }
    loadWeekShiftsForTotals();
    return () => {
      cancelled = true;
    };
  }, [dispatch, filters.structure, filters.endDate, mondayIso, sundayIso]);

  const weekHoursByUserId = useMemo(() => {
    const map: Record<string, number> = {};
    if (filters.endDate || weekShiftsForTotals === null) {
      return map;
    }
    const currentDay = new Date(filters.startDate);
    usersSorted.forEach((u: IUser) => {
      const contract = contracts
        ?.filter((c: IContract) => c.user === u._id)
        .find((c: IContract) => {
          const endDate = c?.endDate && new Date(c.endDate);
          if (
            c?.endDate
            && endDate
            && endDate.getTime() < new Date(filters.startDate).getTime()
          ) {
            return null;
          }
          return c;
        });
      const absencesOptions = contract?.isAnnualized ? ['paid vacation'] : [];
      const otherDays = weekShiftsForTotals.filter(
        (s: IShift) => s.user === u._id
          && !isSameDay(new Date(s.date), currentDay),
      );
      const todayShifts = shiftsList.filter((s: IShift) => s.user === u._id);
      const slices = [...otherDays, ...todayShifts]
        .filter((s: IShift) => s.isActive && !absencesOptions.find((a: string) => a === s.activity))
        .map((s: IShift) => s.slices)
        .flat();
      map[u._id] = slices.length ? (slices.length * 5) / 60 : 0;
    });
    return map;
  }, [
    contracts,
    filters.endDate,
    filters.startDate,
    shiftsList,
    usersSorted,
    weekShiftsForTotals,
  ]);

  function handleChangeFilters(obj: any) {
    if (obj?.structure) {
      localStorage.setItem('structure', obj.structure);
    }
    dispatch({
      type: SET_USERS_FILTERS,
      payload: {
        ...filters,
        ...obj,
      },
    });
  }

  function handleStartDate(dir: string) {
    if (!filters?.startDate) {
      return null;
    }
    const startDate = new Date(`${format(new Date(filters.startDate), 'yyyy-MM-dd')}T00:00:00.000Z`);
    const endDate = filters?.endDate ? new Date(`${format(new Date(filters.endDate), 'yyyy-MM-dd')}T00:00:00.000Z`) : null;
    if (endDate) {
      if (dir === 'prev') {
        startDate.setUTCDate(startDate.getUTCDate() - 7);
        endDate.setUTCDate(endDate.getUTCDate() - 7);
      } else {
        startDate.setUTCDate(startDate.getUTCDate() + 7);
        endDate.setUTCDate(endDate.getUTCDate() + 7);
      }
    } else if (dir === 'prev') {
      startDate.setUTCDate(startDate.getUTCDate() - 1);
    } else {
      startDate.setUTCDate(startDate.getUTCDate() + 1);
    }
    return handleChangeFilters({
      startDate: startDate.toISOString(),
      endDate: endDate ? endDate.toISOString() : null,
    });
  }

  async function navigateToPreview() {
    const currentUrl = window.location.origin;
    const token = localStorage.getItem('token');
    const startDate = filters?.startDate && new Date(`${format(new Date(filters.startDate), 'yyyy-MM-dd')}T00:00:00.000Z`).toISOString();
    const endDate = filters?.endDate && new Date(`${format(new Date(filters.endDate), 'yyyy-MM-dd')}T00:00:00.000Z`).toISOString();
    const query: any = {
      endDate,
      startDate,
      structure: filters.structure,
      planningView,
    };
    if (!endDate) {
      delete query.endDate;
    }
    if (filters?.poles?.length > 0) {
      query.poles = filters.poles.join(',');
    }
    const searchQuery = `?${new URLSearchParams(query)}`;
    const url = `${currentUrl}/export-preview/${token?.replace('JWT ', '')}/${searchQuery}`;
    window.open(url, '_blank');
    return true;
  }

  async function deletePeriod() {
    const data = {
      structure: filters.structure,
      startDate: filters.startDate,
      endDate: filters?.endDate,
      user: planningView !== 'pools',
      line: planningView !== 'users',
    };
    await deleteAllShiftsAction(dispatch, data);
    confirmModalRef?.current?.close();
  }

  async function handleCopyToTemplate() {
    if (!copyWeekName.trim() || !filters.structure || !filters.endDate) return;
    await createFavoriteWeekFromPeriodAction(dispatch, {
      name: copyWeekName.trim(),
      structure: filters.structure,
      startDate: filters.startDate,
      endDate: filters.endDate,
      user: true,
      line: true,
    });
    setCopyWeekName('');
    copyModalRef.current?.close();
  }

  function handleChangePlanning(type: string) {
    dispatch({
      type: SET_PLANNING_VIEW,
      payload: type,
    });
  }

  async function handleChangePublishWeekStatus() {
    const data = {
      structure: filters.structure,
      startDate: new Date(`${format(filters.startDate, 'yyyy-MM-dd')}T00:00:00.000Z`).toISOString(),
      endDate: new Date(`${format(filters.endDate, 'yyyy-MM-dd')}T00:00:00.000Z`).toISOString(),
      isPublished: !isPublished,
    };
    await postPublishWeekAction(dispatch, data);
  }

  useEffect(() => {
    getAllStructuresAction(dispatch);
    getHolidaysAction(dispatch);
  }, []);

  useEffect(() => {
    if (filters.structure) {
      postUsersFilteredAction(dispatch, { ...filters, search: '' });
      postShiftsFilteredAction(dispatch, filters);
      postContractsFilteredAction(dispatch, filters);
      postNotesFilteredAction(dispatch, filters);

      const st = structuresList?.find((s: IStructure) => s._id === filters.structure);
      const canFetchLeaves = user?.role === 'Admin'
        || ((user?.role === 'Owner' || user?.role === 'Manager')
          && user?.canManageLeaves === true
          && st?.manageLeaveRequests === true);
      if (canFetchLeaves) {
        getManagementLeavesAction(dispatch);
      }
    }
  }, [filters, structuresList, user, dispatch]);

  useEffect(() => {
    if (filters?.structure) {
      getStructurePoolsAction(dispatch, filters.structure);
      getStructureAction(dispatch, filters.structure);
      getWeekOptionsAction(dispatch, filters.structure);
      getShiftsListAction(dispatch, filters.structure);
    }
  }, [filters?.structure]);

  useEffect(() => {
    if (!filters.structure) {
      if (user?.structure?._id) {
        handleChangeFilters({
          structure: user.structure._id,
        });
      } else if (savedStructure) {
        handleChangeFilters({
          structure: savedStructure,
        });
      } else if (structuresList.length && user?.role === 'Admin') {
        handleChangeFilters({
          structure: structuresList[0]._id,
        });
      }
    }
  }, [user, structuresList]);

  useEffect(() => {
    if (isWeekView) {
      handleChangeFilters({
        startDate: monday.toISOString(),
        endDate: sunday.toISOString(),
      });
    } else {
      handleChangeFilters({
        endDate: null,
      });
    }
  }, [isWeekView]);

  useEffect(() => {
    if (isMobile) {
      dispatch({
        type: SET_WEEK_VIEW,
        payload: false,
      });
    }
    if (planningView === 'templates' && (isMobile || !canManageWeekTemplates)) {
      dispatch({
        type: SET_PLANNING_VIEW,
        payload: 'global',
      });
    }
  }, [isMobile, planningView, canManageWeekTemplates, dispatch]);

  useEffect(() => {
    if (!filters?.endDate) {
      dispatch({
        type: SET_WEEK_VIEW,
        payload: false,
      });
    }
  }, [filters?.endDate]);

  useEffect(() => {
    if (isWeekView && filters.endDate) {
      const daysOptions: DayType[] = [];
      let current = 0;
      const end = 6;
      while (current <= end) {
        const newDate = new Date(Date.UTC(
          monday.getUTCFullYear(),
          monday.getUTCMonth(),
          monday.getUTCDate() + current,
        ));
        daysOptions.push({
          date: newDate.toISOString(),
          day: newDate.getUTCDate(),
          weekDay: new Intl.DateTimeFormat('fr', { weekday: 'long', day: 'numeric' }).format(newDate),
        });
        current += 1;
      }
      setWeek(daysOptions);
    } else {
      setWeek([]);
    }
  }, [isWeekView, filters.endDate]);

  useEffect(() => {
    if (note) {
      noteModalRef.current.open();
    } else {
      noteModalRef.current.close();
    }
  }, [note]);

  return (
    <div className={styles.container}>
      <Header name='Planning' isProfile />
      <div className={styles.content}>
        <div className={styles.nav}>
          <button
            type='button'
            onClick={() => handleChangePlanning('pools')}
            className={planningView === 'pools' ? styles.active : ''}
          >
            Équipements
            <div className={styles.bar} />
          </button>
          <button
            type='button'
            onClick={() => handleChangePlanning('users')}
            className={planningView === 'users' ? styles.active : ''}
          >
            Membres
            <div className={styles.bar} />
          </button>
          <button
            type='button'
            onClick={() => handleChangePlanning('global')}
            className={planningView === 'global' ? styles.active : ''}
          >
            Global
            <div className={styles.bar} />
          </button>
          {!isMobile && canManageWeekTemplates && (
            <button
              type='button'
              onClick={() => handleChangePlanning('templates')}
              className={planningView === 'templates' ? styles.active : ''}
            >
              Modèles de semaines
              <div className={styles.bar} />
            </button>
          )}
        </div>
        {planningView === 'templates' && !isMobile && canManageWeekTemplates
          ? (
            <WeekTemplatesTab
              structureId={filters.structure}
              structuresList={structuresList}
              usersList={usersList}
              pools={pools}
              user={user}
              isMobile={!!isMobile}
            />
          )
          : <>
        <div className={styles.filters}>
          {!isMobile && <div className={styles.row}>
            <div className={styles['select-date']}>
              <button
                type='button'
                onClick={() => handleStartDate('prev')}
              >
                <FaSortDown />
              </button>
              <div className={styles.dates}>
                <h4>
                  {isWeekView ? `${mondayFormated} > ${sundayFormated}` : dateFormated}
                  {/* {!isWeekView
                    && <Tooltip
                      message={!isHoliday?._id
                        ? 'Ajouter le jour férié' : 'Supprimer le jour férié'}
                    >
                      <span
                      className={`${styles.holiday} ${!isHoliday?._id && user?.role !== 'Admin'
                        ? styles.none : ''} ${isHoliday?._id ? styles.active : ''}`}
                      onClick={() => handleHoliday()}
                    >
                      <BsEmojiSunglassesFill />
                    </span>
                    </Tooltip>
                  } */}
                  <span><br />Semaine {weekNumber}
                  </span>
                </h4>
              </div>
              <button
                type='button'
                onClick={() => handleStartDate('next')}
              >
                <FaSortDown />
              </button>
            </div>
            {!isMobile && filters?.startDate && <DayPicker
              handleChangeFilters={handleChangeFilters}
            />}
            <div className={`${styles.col}`}>
              <DropDown
                onChange={(value: any) => dispatch({
                  type: SET_WEEK_VIEW,
                  payload: value,
                })}
                selected={isWeekView}
                options={[
                  { label: 'Vue jour', value: false },
                  { label: 'Vue semaine', value: true },
                ]}
              />
            </div>
          </div>
          }
          <div className={styles.row}>
            <div className={styles.col}>
              {(user?.role === 'Admin' || user?.role === 'Owner') && structuresList
                && <DropDown
                  onChange={(value: any) => handleChangeFilters({ structure: value, poles: [] })}
                  selected={filters?.structure}
                  options={[
                    ...structuresList.map((s: IStructure) => ({ label: s.name, value: s._id })),
                  ]}
                />
              }
            </div>
            {poles && !isMobile
              && <div className={styles.col}>
                <DropDown
                  onChange={(value: any) => handleChangeFilters({ poles: value })}
                  selected={filters?.poles}
                  multiLabel={filters?.poles.length > 0 ? 'catégories séléctionnées' : 'Catégories'}
                  isMulti
                  options={[
                    { label: 'Toutes les catégories', value: null },
                    ...poles.map((s: IPole) => ({
                      label: s.name,
                      value: s._id,
                      color: s.color,
                    })),
                  ]}
                />
              </div>
            }
            {!isMobile
              && isImport
              && isWeekView
              && canManageWeekTemplates
              && <div className={styles.action}>
                <Tooltip
                  message={'Importer une semaine'}
                ><button
                  type='button'
                  className={styles.import}
                  onClick={() => modalRef.current.open()}
                >
                    <img src={calendar} alt='import-week' />
                  </button>
                </Tooltip>
              </div>
            }
            {!isMobile && shiftsList.length > 0
              && <div className={styles.action}>
                <Tooltip
                  message={'Voir l\'export'}
                >
                  <ExportButton
                    callBack={() => navigateToPreview()}
                  />
                </Tooltip>
              </div>}
            {!isMobile && shiftsList.length > 0 && isWeekView
              && (user?.role === 'Admin' || (user?.role !== 'Member' && user?.isReadOnly === false && user?.canPublishWeek))
              && <div className={styles.action}>
                <Tooltip
                  message={!isPublished ? 'Publier la semaine' : 'Supprimer la publication'}
                ><button
                  type='button'
                  className={`${styles.publish} ${isPublished ? styles.active : ''}`}
                  onClick={() => handleChangePublishWeekStatus()}
                >
                    <FaCalendarCheck />
                  </button>
                </Tooltip>
              </div>
            }
          </div>
          {isMobile
            && <div className={styles.row}>
              <div className={styles['select-date-mobile']}>
                <SelectDate
                />
              </div>
            </div>
          }
        </div>
        <div className={styles.labels}>
          <div className={styles.row}>

          </div>
          <div className={styles.row}>
            {!filters.endDate && list?.shifts?.map((s: any) => {
              const label = s.label?.includes(':00') && s.label !== '02:00' && s.label !== structure?.schedules?.end ? s.label : null;
              return (
                <div key={s.label}
                  style={{ width: `${100 / list.shifts.length}%` }}
                  className={styles.hour}
                >
                  <p>{label}</p>
                </div>
              );
            })}
            {filters.endDate && week?.map((s: any) => (
              <div key={s.date}
                style={{ width: `${100 / 7}%` }}
                className={styles.hour}
              >
                <p>{s.weekDay}</p>
              </div>
            ))}
          </div>
        </div>
        <div className={styles['mobile-filters']}>
          <h4>{dateFormated}</h4>
          {poles
            && <button
              onClick={() => setFiltersIsOpen(!filtersIsOpen)}
            >
              <IoFilter />
              {filters.poles.length > 0
                && <div className={styles.count}>
                  <p>{filters.poles.length}</p>
                </div>
              }
            </button>
          }
        </div>
        {isLoading && isLoading.includes('GET_ALL_SHIFTS') ? (
          <div className={styles.listLoader}>
            <Loader />
          </div>
        ) : (
          <div className={styles.list}>
            <div className={styles.pools}>
            {planningView !== 'users' && poles && filters.structure && shiftsList
              && pools?.map((p: IPool) => (
                <div key={p._id} className={styles.grid}>
                  {p.lines.map((line: ILine, index: number) => <PlanningRow
                    key={line._id}
                    line={line}
                    lines={p.lines}
                    pool={p}
                    name={index === 0 ? p.name : null}
                    isMobile={!!isMobile}
                    week={week}
                    shifts={shiftsList
                      .filter((s: IShift) => s.line === line._id)}
                  />)}
                </div>
              ))}
          </div>
          <div className={styles.grid}>
            {planningView !== 'pools' && poles && filters.structure && shiftsList && usersList
              && usersSorted
                .filter((u: IUser) => {
                  if (!activesContracts.find((a: IContract) => a.user === u._id)) {
                    return null;
                  }
                  if (user?.role === 'Manager') {
                    if (u.poles.find((p) => user.poles.find((pu: string) => p === pu))) {
                      return u;
                    }
                    return null;
                  }
                  return u;
                })
                .map((u: IUser) => <PlanningRow
                  key={u._id}
                  user={u}
                  isMobile={!!isMobile}
                  week={week}
                  shifts={shiftsList
                    .filter((s: IShift) => s.user === u._id)}
                  leavePicto={leavePictoByUserId[u._id]}
                  weekTotalHours={
                    filters.endDate ? undefined : weekHoursByUserId[u._id]
                  }
                />)}
          </div>
          {showListActions
            && <div className={styles.listActions}>
              {showCopyWeekToTemplateButton
                && <button
                  type="button"
                  className={styles.copy}
                  onClick={() => copyModalRef.current?.open()}
                >
                  Copier vers un modèle
                </button>
              }
              {user?.role === 'Admin'
                && <button
                  type="button"
                  className={styles.delete}
                  onClick={() => confirmModalRef.current.open()}
                >
                  Supprimer la vue
                </button>
              }
            </div>
          }
          </div>
        )}
      </>
        }
      </div>
      <FiltersMobile
        poles={poles}
        filters={filters}
        isOpen={filtersIsOpen}
        setIsOpen={setFiltersIsOpen}
      />
      <ModalStandalone ref={confirmModalRef} minWidth={450} isClose={false}>
        <ConfirmModal
          message={'Êtes-vous sûr de vouloir supprimer cette vue?'}
          confirmAction={() => deletePeriod()}
          abort={() => confirmModalRef?.current?.close()}
        />
      </ModalStandalone>
      <ModalStandalone ref={copyModalRef} minWidth={450}>
        <div className={templateStyles.modal}>
          <h4>Copier la semaine vers un modèle</h4>
          <input
            value={copyWeekName}
            onChange={(e) => setCopyWeekName(e.target.value)}
            placeholder="Nom du modèle"
          />
          <div className={templateStyles.modalActions}>
            <button type="button" onClick={() => copyModalRef.current?.close()}>Annuler</button>
            <button type="button" onClick={() => handleCopyToTemplate()}>Créer</button>
          </div>
        </div>
      </ModalStandalone>
      <ModalStandalone ref={modalRef} isDraggable>
        <ImportModal
          close={() => modalRef?.current?.close()}
        />
      </ModalStandalone>
      <ModalStandalone
        ref={noteModalRef}
        closeCallBack={() => {
          dispatch({
            type: GET_NOTE,
            payload: null,
          });
        }}
      >
        <NoteModal />
      </ModalStandalone>
    </div>
  );
};

export default AdminPlanning;
